import { styled } from "goober";
import { useState } from "react";
import { Text } from "../Text";
import { AiProviderModelSpendChart } from "./AiProviderModelSpendChart";
import type { AiProviderModelCostRow } from "./aiProviderMonitoring";
import { formatEstimatedCostUsd } from "./aiUsage";
import { formatModelName } from "../../lib/formatModelName";
import { Bar, BarColumn, BarRow, ColumnMeta, Columns } from "./monitoringBars";
import {
  MONITORING_SHORT_VIEWPORT_HEIGHT,
  SPACE_COLORS,
} from "./monitoringPanelLayout";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { Panel, PanelHeader, TotalRow } from "./workflowsLayout";

// In the collapsed modes (single row when short, single column when narrow) the
// panel gets a tall card to fill, so it flips from the compact stacked bar to
// vertical bars — like Execution volume by space. The normal layout, where the
// panel hugs its content, keeps the stacked bar + legend.
const COLLAPSED = `@media (max-width: 900px), (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px)`;

const VerticalOnly = styled("div")`
  display: none;
  ${COLLAPSED} {
    display: flex;
    flex: 1;
    min-height: 0;
  }
`;

const StackedOnly = styled("div")`
  margin-top: auto;
  ${COLLAPSED} {
    display: none;
  }
`;

type Props = { modelRows: readonly AiProviderModelCostRow[] };

export function AiSpendByModelPanel({ modelRows }: Props) {
  const [hovered, setHovered] = useState<string | null>(null);
  const total = modelRows.reduce(
    (sum, row) => sum + (row.estimatedCostUsd ?? 0),
    0
  );
  const maxSpend = Math.max(
    0,
    ...modelRows.map((r) => r.estimatedCostUsd ?? 0)
  );
  const isEmpty = modelRows.length === 0;

  const emptyText = (
    <Text variant="text-small" color="var(--text-secondary)">
      No usage recorded in the last 30 days.
    </Text>
  );

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Spend by model</Text>
        <TotalRow>
          <Text variant="display-md">{formatEstimatedCostUsd(total)}</Text>
        </TotalRow>
      </PanelHeader>

      <VerticalOnly>
        {isEmpty ? (
          emptyText
        ) : (
          <Columns>
            {modelRows.map((row, i) => {
              const spend = row.estimatedCostUsd ?? 0;
              const active = hovered === row.model;
              return (
                <BarColumn
                  key={row.model}
                  onMouseEnter={() => setHovered(row.model)}
                  onMouseLeave={() => setHovered(null)}
                  style={{ opacity: hovered != null && !active ? 0.4 : 1 }}
                >
                  <BarRow>
                    <Bar
                      style={{
                        width: active ? 12 : 6,
                        background: SPACE_COLORS[i % SPACE_COLORS.length]!,
                        minHeight: spend > 0 ? 6 : 0,
                        height: `${maxSpend > 0 ? (spend / maxSpend) * 100 : 0}%`,
                      }}
                    />
                  </BarRow>
                  <ColumnMeta>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {formatModelName(row.model)}
                    </Text>
                    <Text
                      variant="text-small-md"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {formatEstimatedCostUsd(spend)}
                    </Text>
                  </ColumnMeta>
                </BarColumn>
              );
            })}
          </Columns>
        )}
      </VerticalOnly>

      <StackedOnly>
        {isEmpty ? (
          emptyText
        ) : (
          <AiProviderModelSpendChart modelRows={modelRows} />
        )}
      </StackedOnly>
    </Panel>
  );
}
